/**
 * 설정 · 알림 탭.
 *
 * 화면의 질문은 둘뿐이다 — **어느 방으로 보낼까**(채널), **무엇을 보낼까**(이벤트).
 * 그리고 「왜 안 왔지」 를 혼자 확인할 수 있게 최근 발송 기록을 같이 둔다.
 * 기록이 없으면 사장님은 채널 주소를 의심하고 우리에게 전화한다.
 */

import { useState, useTransition } from "react";
import { toast } from "sonner";

import EmptyState from "@buildpy/ui/empty-state";
import Pill, { PillTone } from "@buildpy/ui/pill";
import SaveBar, { InstantSaveNote } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import ListTable, { type ListTableColumn } from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { formatDateTime, formatNumber } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import {
  UNANSWERED_HOURS_LABEL,
  UNANSWERED_HOURS_OPTIONS,
  UnansweredHoursOption,
  isUnansweredHoursOption,
} from "@buildpy/core/modules/customer/unanswered";
import { saveUnansweredHours } from "@/api/actions/customer";
import { saveCustomerNotifyOff } from "@/api/actions/customer-notify";
import {
  CUSTOMER_NOTIFY_CHANNEL_LABEL,
  CUSTOMER_NOTIFY_EVENT_LABEL,
  CUSTOMER_NOTIFY_EVENTS,
  CustomerNotifyChannelName,
  CustomerNotifyEvent,
  CustomerNotifyMonthCount,
} from "@buildpy/core/modules/customer-notify/types";
import { saveNotifyChannels } from "@/api/actions/notify";
import {
  NOTIFY_CHANNEL_LABEL,
  NOTIFY_CHANNEL_TYPES,
  NOTIFY_EVENT_LABEL,
  NOTIFY_EVENTS,
  NotifyChannel,
  NotifyChannelType,
  NotifyEvent,
  NotifyStatus,
} from "@buildpy/core/modules/notify/types";

/** 표에 필요한 것만. 로그 전체를 클라이언트로 넘기지 않는다. */
export interface NotifyLogRow {
  id: string;
  created_at: string;
  event: NotifyEvent;
  channel_type: NotifyChannelType;
  status: NotifyStatus;
  error: string | null;
}

// 고객 알림 기록 한 줄 — 가린 번호만 넘긴다.
export interface CustomerNotifyLogRow {
  id: string;
  created_at: string;
  event: CustomerNotifyEvent;
  channel: CustomerNotifyChannelName;
  recipient_masked: string | null;
  status: NotifyStatus;
  error: string | null;
}

export interface CustomerNotifyPanel {
  off: CustomerNotifyEvent[];
  thisMonth: CustomerNotifyMonthCount;
  logs: CustomerNotifyLogRow[];
  // 환경변수 오류 문구 — 있으면 경고를 띄운다.
  configError: string | null;
}

interface Props {
  customer: CustomerNotifyPanel;
  // 미응답 경고 기준 시간.
  unansweredHours: UnansweredHoursOption;
  channels: NotifyChannel[];
  logs: NotifyLogRow[];
  /** 실장은 읽기만 한다. 칸을 감추지 않고 잠근다. */
  canEdit: boolean;
  /** dry-run 이면 실제로 나가지 않는다 — 화면이 먼저 말해 준다. */
  dryRun: boolean;
}

const STATUS_LABEL: Record<NotifyStatus, string> = {
  sent: "보냄",
  failed: "실패",
  skipped: "안 보냄",
};

// 보냄=완료 초록 · 실패=위험 빨강 · 안 보냄(끔·시험 모드·번호 없음)=조용한 회색.
const STATUS_TONE: Record<NotifyStatus, PillTone> = {
  sent: "done",
  failed: "danger",
  skipped: "neutral",
};

// 발송 기록 표 칸 — 공통 Table 기본(h-12·p-4)보다 촘촘하게.
const logColumns = (labels: string[]): ListTableColumn[] => labels.map((label) => ({ label, wrap: true, className: "h-auto py-2" }));
const LOG_CELL = "px-3 py-2";

const emptyChannel = (): NotifyChannel => ({
  type: "dooray",
  webhook_url: "",
  // 새 채널은 전부 구독으로 시작한다 — 켜 두고 빼는 쪽이 하나씩 켜는 쪽보다 빠르다.
  events: [...NOTIFY_EVENTS],
});

const UnansweredSection = ({ value, canEdit }: { value: UnansweredHoursOption; canEdit: boolean }) => {
  const [hours, setHours] = useState<UnansweredHoursOption>(value);
  const [pending, startTransition] = useTransition();

  const change = (next: string) => {
    if (!isUnansweredHoursOption(next)) return;
    const prev = hours;
    setHours(next);
    startTransition(async () => {
      try {
        const result = await saveUnansweredHours(next);
        if (result.ok) toast.success(result.message);
        else {
          setHours(prev);
          toast.error(result.message);
        }
      } catch (caught) {
        if (settleRedirect(caught)) return;
        setHours(prev);
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <section aria-label="미응답 경고" className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-sm font-semibold">미응답 경고</h2>
        <p className="text-xs text-muted-foreground">
          간편접수·홈페이지·공사 안내문 문의가 이 시간 넘게 응대 전이면 알림 채널로 한 번 알립니다.
        </p>
      </div>
      <div className="flex items-center gap-x-2">
        {canEdit && <InstantSaveNote />}
        <SimpleSelect
          aria-label="미응답 경고"
          className="w-32"
          disabled={!canEdit || pending}
          options={UNANSWERED_HOURS_OPTIONS.map((option) => ({
            value: option,
            label: UNANSWERED_HOURS_LABEL[option],
          }))}
          value={hours}
          onValueChange={change}
        />
      </div>
    </section>
  );
};

interface CustomerNotifySectionProps {
  customer: CustomerNotifyPanel;
  canEdit: boolean;
  off: CustomerNotifyEvent[];
  onOffChange: (next: CustomerNotifyEvent[]) => void;
  pending: boolean;
}

// 끈 사건은 탭 아래 저장 바가 채널과 함께 저장한다.
const CustomerNotifySection = ({ customer, canEdit, off, onOffChange, pending }: CustomerNotifySectionProps) => {
  const toggle = (event: CustomerNotifyEvent, on: boolean) =>
    onOffChange(on ? off.filter((e) => e !== event) : [...off.filter((e) => e !== event), event]);

  return (
    <section aria-label="고객 알림" className="flex flex-col gap-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">고객 알림</h2>
          <p className="text-xs text-muted-foreground">
            고객 휴대폰으로 알림톡(실패하면 문자)을 보냅니다. 발신명은 「빌드파이」입니다.
          </p>
        </div>
        <p className="text-sm">
          이번 달 발송 <span className="font-semibold tabular-nums">{formatNumber(customer.thisMonth.sent)}</span>건
          {customer.thisMonth.logOnly > 0 && (
            <span className="text-muted-foreground">
              {" · 기록만 "}
              <span className="tabular-nums">{formatNumber(customer.thisMonth.logOnly)}</span>건
            </span>
          )}
        </p>
      </div>

      <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
        알림톡 연결 전에는 기록만 남습니다.
      </p>
      {customer.configError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">
          {customer.configError}
        </p>
      )}

      <fieldset className="flex flex-wrap gap-x-4 gap-y-2" disabled={!canEdit || pending}>
        {CUSTOMER_NOTIFY_EVENTS.map((event) => (
          <label key={event} className="flex items-center gap-1.5 text-sm">
            <Checkbox
              checked={!off.includes(event)}
              disabled={!canEdit || pending}
              onCheckedChange={(checked) => toggle(event, checked === true)}
            />
            {CUSTOMER_NOTIFY_EVENT_LABEL[event]}
          </label>
        ))}
      </fieldset>

      {customer.logs.length === 0 ? (
        <EmptyState>
          아직 고객 알림 기록이 없습니다.
        </EmptyState>
      ) : (
        <ListTable
          columns={logColumns(["시각", "이벤트", "받는 번호", "채널", "상태", "사유"])}
          headerVariant="log"
          tableClassName="min-w-[40rem]">
          {customer.logs.map((log) => (
            <TableRow key={log.id}>
              <TableCell className={`${LOG_CELL} whitespace-nowrap text-muted-foreground`}>{formatDateTime(log.created_at)}</TableCell>
              <TableCell className={`${LOG_CELL} whitespace-nowrap`}>{CUSTOMER_NOTIFY_EVENT_LABEL[log.event]}</TableCell>
              <TableCell className={`${LOG_CELL} whitespace-nowrap tabular-nums`}>{log.recipient_masked ?? "—"}</TableCell>
              <TableCell className={`${LOG_CELL} whitespace-nowrap`}>{CUSTOMER_NOTIFY_CHANNEL_LABEL[log.channel]}</TableCell>
              <TableCell className={`${LOG_CELL} whitespace-nowrap`}>
                <Pill tone={STATUS_TONE[log.status]}>{STATUS_LABEL[log.status]}</Pill>
              </TableCell>
              <TableCell className={`${LOG_CELL} text-muted-foreground`}>{log.error ?? ""}</TableCell>
            </TableRow>
          ))}
        </ListTable>
      )}
    </section>
  );
};

const SettingsNotify = ({ customer, unansweredHours, channels, logs, canEdit, dryRun }: Props) => {
  const [rows, setRows] = useState<NotifyChannel[]>(() =>
    channels.map((channel) => ({ ...channel, events: [...channel.events] }))
  );
  const [pending, startTransition] = useTransition();
  const [off, setOff] = useState<CustomerNotifyEvent[]>(() => [...customer.off]);
  // 마지막으로 저장된 모양 — 이것과 달라야 저장 바가 켜진다.
  const [savedRows, setSavedRows] = useState(() => JSON.stringify(rows));
  const [savedOff, setSavedOff] = useState(() => JSON.stringify([...customer.off].sort()));
  const rowsDirty = JSON.stringify(rows) !== savedRows;
  const offDirty = JSON.stringify([...off].sort()) !== savedOff;

  const patch = (index: number, next: Partial<NotifyChannel>) =>
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...next } : row))
    );

  const toggleEvent = (index: number, event: NotifyEvent, on: boolean) =>
    setRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              events: on
                ? [...row.events, event]
                : row.events.filter((e) => e !== event),
            }
          : row
      )
    );

  // 채널·고객 알림 중 고친 쪽만 차례로 보낸다 — 앞이 실패하면 뒤는 보내지 않는다.
  const save = () => {
    const nextRows = rows;
    const nextOff = off;
    startTransition(async () => {
      try {
        if (rowsDirty) {
          const result = await saveNotifyChannels(nextRows);
          if (!result.ok) {
            toast.error(result.message);
            return;
          }
          toast.success(result.message);
          setSavedRows(JSON.stringify(nextRows));
        }
        if (offDirty) {
          const result = await saveCustomerNotifyOff(nextOff);
          if (!result.ok) {
            toast.error(result.message);
            return;
          }
          toast.success(result.message);
          setSavedOff(JSON.stringify([...nextOff].sort()));
        }
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <div className="flex flex-col gap-y-6">
      <section className="flex flex-col gap-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">알림 채널</h2>
            <p className="text-xs text-muted-foreground">
              두레이·슬랙 수신 웹훅 주소를 넣고, 받을 사건을 고릅니다.
            </p>
          </div>
          <Button
            disabled={!canEdit || pending}
            onClick={() => setRows((prev) => [...prev, emptyChannel()])}
            size="sm"
            type="button"
            variant="outline">
            채널 추가
          </Button>
        </div>

        {dryRun && (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            지금은 시험 모드라 실제로 보내지 않습니다.
          </p>
        )}

        {rows.length === 0 ? (
          <EmptyState>
            등록된 채널이 없습니다. 추가하면 새 상담·견적 발송이 채팅방으로 갑니다.
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-y-3">
            {rows.map((row, index) => (
              <SectionCard
                key={index}
                as="li"
                className="flex flex-col gap-3">
                <fieldset
                  className="flex flex-col gap-3"
                  disabled={!canEdit || pending}>
                  <div className="flex flex-wrap items-center gap-2">
                    <SimpleSelect
                      aria-label="채널 유형"
                      className="w-28"
                      disabled={!canEdit || pending}
                      options={NOTIFY_CHANNEL_TYPES.map((type) => ({
                        value: type,
                        label: NOTIFY_CHANNEL_LABEL[type],
                      }))}
                      value={row.type}
                      onValueChange={(next) =>
                        patch(index, { type: next as NotifyChannelType })
                      }
                    />

                    <div className="min-w-48 flex-1">
                      <Input
                        aria-label="웹훅 주소"
                        name="webhook_url"
                        onChange={(e) =>
                          patch(index, { webhook_url: e.target.value })
                        }
                        placeholder="https://hook.dooray.com/services/..."
                        value={row.webhook_url}
                      />
                    </div>

                    <Button
                      onClick={() =>
                        setRows((prev) => prev.filter((_, i) => i !== index))
                      }
                      size="sm"
                      type="button"
                      variant="ghost">
                      삭제
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {NOTIFY_EVENTS.map((event) => (
                      <label
                        key={event}
                        className="flex items-center gap-1.5 text-sm">
                        <Checkbox
                          checked={row.events.includes(event)}
                          disabled={!canEdit || pending}
                          onCheckedChange={(checked) =>
                            toggleEvent(index, event, checked === true)
                          }
                        />
                        {NOTIFY_EVENT_LABEL[event]}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </SectionCard>
            ))}
          </ul>
        )}

        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            읽기 전용입니다. 바꾸려면 대표에게 요청하세요.
          </p>
        )}
      </section>

      <UnansweredSection canEdit={canEdit} value={unansweredHours} />

      <section className="flex flex-col gap-y-2">
        <h2 className="text-sm font-semibold">최근 발송</h2>
        {logs.length === 0 ? (
          <EmptyState>
            아직 보낸 알림이 없습니다.
          </EmptyState>
        ) : (
          <ListTable
            columns={logColumns(["시각", "이벤트", "채널", "상태", "비고"])}
            headerVariant="log"
            tableClassName="min-w-[34rem]">
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className={`${LOG_CELL} whitespace-nowrap text-muted-foreground`}>
                  {formatDateTime(log.created_at)}
                </TableCell>
                <TableCell className={`${LOG_CELL} whitespace-nowrap`}>
                  {NOTIFY_EVENT_LABEL[log.event]}
                </TableCell>
                <TableCell className={`${LOG_CELL} whitespace-nowrap`}>
                  {NOTIFY_CHANNEL_LABEL[log.channel_type]}
                </TableCell>
                <TableCell className={`${LOG_CELL} whitespace-nowrap`}>
                  <Pill tone={STATUS_TONE[log.status]}>{STATUS_LABEL[log.status]}</Pill>
                </TableCell>
                <TableCell className={`${LOG_CELL} text-muted-foreground`}>
                  {log.error ?? ""}
                </TableCell>
              </TableRow>
            ))}
          </ListTable>
        )}
      </section>

      <CustomerNotifySection
        canEdit={canEdit}
        customer={customer}
        off={off}
        onOffChange={setOff}
        pending={pending}
      />

      {canEdit && (
        <SaveBar dirty={rowsDirty || offDirty} onSave={save} pending={pending} />
      )}
    </div>
  );
};

export default SettingsNotify;
